import { ArrowUpRight, Globe, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

/**
 * Shows an external record (FEI, HorseTelex...) inside the product.
 * Probes /public/embed-check first: iframe when the host allows framing,
 * an elegant open-in-new-tab fallback when it does not.
 */
export const ExternalRecordDialog = ({ link, onClose }) => {
  const { t } = useTranslation();
  const [embeddable, setEmbeddable] = useState(undefined);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const [preview, setPreview] = useState(null);
  const host = hostOf(link.url);

  useEffect(() => {
    let alive = true;
    setEmbeddable(undefined);
    setFrameLoaded(false);
    setPreview(null);
    api
      .get("/public/embed-check", { params: { url: link.url } })
      .then(({ data }) => alive && setEmbeddable(!!data.embeddable))
      .catch(() => alive && setEmbeddable(false));
    return () => {
      alive = false;
    };
  }, [link.url]);

  // Blocked embeds get an enriched preview card (server-side OG metadata,
  // SSRF-guarded + cached) — the flow stays in-app (client review item 15).
  useEffect(() => {
    if (embeddable !== false) return undefined;
    let alive = true;
    api
      .get("/public/embed-preview", { params: { url: link.url } })
      .then(({ data }) => alive && setPreview(data))
      .catch(() => alive && setPreview({ fetched: false, host }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embeddable, link.url]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="external-record-dialog"
        className="max-w-4xl w-[calc(100vw-2rem)] rounded-2xl p-0 gap-0 overflow-hidden"
      >
        <DialogHeader className="px-6 py-4 border-b border-black/5 flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-base font-medium flex items-center gap-2 min-w-0">
            <Globe className="w-4 h-4 text-[#1D4D3B] shrink-0" aria-hidden="true" />
            <span className="truncate">{link.label}</span>
            <span className="text-xs font-normal text-[#737373] truncate hidden sm:inline">{host}</span>
          </DialogTitle>
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="external-record-open-tab"
            className="mr-8 inline-flex items-center gap-1.5 px-4 h-9 rounded-full bg-black/5 text-sm font-medium text-[#0A0A0A] hover:bg-black/10 transition-colors duration-200 shrink-0"
          >
            {t("pub.openExternal")}
            <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
          </a>
        </DialogHeader>

        <div className="h-[70vh] bg-[#F9F9F9] relative">
          {embeddable === undefined && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#737373]" data-testid="external-record-loading">
              <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" />
              <span className="text-sm">{t("pub.loadingRecord")}</span>
            </div>
          )}

          {embeddable === true && (
            <>
              {!frameLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#737373]">
                  <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" />
                  <span className="text-sm">{t("pub.loadingRecord")}</span>
                </div>
              )}
              <iframe
                src={link.url}
                title={link.label}
                onLoad={() => setFrameLoaded(true)}
                data-testid="external-record-iframe"
                sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                className={`w-full h-full border-0 transition-opacity duration-500 ${frameLoaded ? "opacity-100" : "opacity-0"}`}
              />
            </>
          )}

          {embeddable === false && (
            <div
              className="absolute inset-0 flex items-center justify-center px-6 py-8 overflow-y-auto"
              data-testid="external-record-fallback"
            >
              {preview === null ? (
                <div className="flex flex-col items-center gap-3 text-[#737373]">
                  <Loader2 className="w-6 h-6 animate-spin" aria-hidden="true" />
                  <span className="text-sm">{t("pub.loadingRecord")}</span>
                </div>
              ) : (
                <div className="w-full max-w-lg rounded-3xl bg-white border border-black/5 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.18)] overflow-hidden" data-testid="external-record-preview-card">
                  {preview.image && (
                    <img src={preview.image} alt="" className="w-full h-44 object-cover" loading="lazy" />
                  )}
                  <div className="p-7 flex flex-col gap-4">
                    <div className="flex items-center gap-2.5">
                      {preview.favicon ? (
                        <img src={preview.favicon} alt="" className="w-5 h-5 rounded" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <Globe className="w-4 h-4 text-[#1D4D3B]" aria-hidden="true" />
                      )}
                      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/45" data-testid="external-record-provider">
                        {preview.provider?.name || preview.site_name || preview.host || host}
                        {preview.provider?.kind ? ` · ${t(`pub.recordKind_${preview.provider.kind}`)}` : ""}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <h3 className="text-lg font-medium tracking-tight text-[#0A0A0A] leading-snug" data-testid="external-record-preview-title">
                        {preview.title || link.label}
                      </h3>
                      {preview.description && (
                        <p className="text-sm text-[#737373] leading-relaxed line-clamp-3">{preview.description}</p>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-4 pt-2 border-t border-black/5">
                      <span className="text-[11px] text-[#737373]/80 max-w-[220px] leading-snug">
                        {t("pub.recordCardNote", { host: preview.host || host })}
                      </span>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="external-record-fallback-open"
                        className="inline-flex items-center gap-2 px-6 h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium hover:bg-black/80 transition-colors duration-300 shrink-0"
                      >
                        {t("pub.openFullRecord")}
                        <ArrowUpRight className="w-4 h-4" aria-hidden="true" />
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
